import type { DisplayTimeZone, RelativePreset } from './types';

export const RELATIVE_PRESETS: {
  key: RelativePreset;
  label: string;
  getRange: (tz?: string) => { from: Date; to: Date };
}[] = [
  {
    key: '30min',
    label: 'Last 30 minutes',
    getRange: () => rangeMs(30 * 60 * 1000),
  },
  { key: '1h', label: 'Last 1 hour', getRange: () => rangeMs(60 * 60 * 1000) },
  {
    key: '6h',
    label: 'Last 6 hours',
    getRange: () => rangeMs(6 * 60 * 60 * 1000),
  },
  {
    key: '12h',
    label: 'Last 12 hours',
    getRange: () => rangeMs(12 * 60 * 60 * 1000),
  },
  {
    key: '1d',
    label: 'Last 1 day',
    getRange: () => rangeMs(24 * 60 * 60 * 1000),
  },
  {
    key: '7d',
    label: 'Last 7 days',
    getRange: (tz?: string) => rangeDays(7, tz),
  },
  {
    key: '14d',
    label: 'Last 14 days',
    getRange: (tz?: string) => rangeDays(14, tz),
  },
  {
    key: '30d',
    label: 'Last 30 days',
    getRange: (tz?: string) => rangeDays(30, tz),
  },
  { key: '3m', label: 'Last 3 months', getRange: () => rangeMonths(3) },
  { key: '6m', label: 'Last 6 months', getRange: () => rangeMonths(6) },
  { key: '12m', label: 'Last 12 months', getRange: () => rangeMonths(12) },
];

export function rangeMs(ms: number): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to.getTime() - ms);
  return { from, to };
}

// Returns the UTC Date that represents local midnight on (today - daysAgo) in
// the given IANA timezone. Uses the Intl guess-and-offset technique so DST
// transitions are handled correctly (no fixed 24h assumption).
function localMidnightUTC(tz: string, daysAgo: number, now: Date): Date {
  // Step 1: find today's local calendar date in the given timezone.
  const dateDtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const dateParts = dateDtf.formatToParts(now);
  const getDate = (type: string): number =>
    Number(dateParts.find(p => p.type === type)?.value ?? '0');
  const y = getDate('year');
  const mo = getDate('month'); // 1-based
  const d = getDate('day');

  // Step 2: subtract daysAgo calendar days. Date.UTC handles month/year rollover.
  const guess = new Date(Date.UTC(y, mo - 1, d - daysAgo, 0, 0, 0, 0));

  // Step 3: verify what local clock time `guess` maps to in `tz` and compute offset.
  const verifyDtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const verifyParts = verifyDtf.formatToParts(guess);
  const getVerify = (type: string): number =>
    Number(verifyParts.find(p => p.type === type)?.value ?? '0');
  const asIfUtcMs = Date.UTC(
    getVerify('year'),
    getVerify('month') - 1,
    getVerify('day'),
    getVerify('hour'),
    getVerify('minute'),
    getVerify('second')
  );
  const offsetMs = asIfUtcMs - guess.getTime();

  // Adjusting by the offset converts the UTC-midnight guess to local midnight.
  return new Date(guess.getTime() - offsetMs);
}

// Computes a calendar-day-aligned range: from = local midnight (days-1) days
// ago in the given timezone, to = now. This ensures day-level bucket labels
// always include today (in the user's timezone) as the last bucket.
export function rangeDays(days: number, tz?: string): { from: Date; to: Date } {
  const to = new Date();
  const resolvedTz =
    !tz || tz === 'browser'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      : tz;
  const from = localMidnightUTC(resolvedTz, days - 1, to);
  return { from, to };
}

export function calculateMonthRange(to: Date, months: number) {
  // Compute the target year/month explicitly to avoid Date#setMonth
  // rollover issues when the current day does not exist in the
  // target month (e.g. May 31 -> February).
  let targetYear = to.getFullYear();
  let targetMonth = to.getMonth() - months; // 0-based month index

  while (targetMonth < 0) {
    targetMonth += 12;
    targetYear -= 1;
  }

  const currentDay = to.getDate();
  const lastDayOfTargetMonth = new Date(
    targetYear,
    targetMonth + 1,
    0
  ).getDate();

  const clampedDay = Math.min(currentDay, lastDayOfTargetMonth);

  const from = new Date(
    targetYear,
    targetMonth,
    clampedDay,
    to.getHours(),
    to.getMinutes(),
    to.getSeconds(),
    to.getMilliseconds()
  );

  return { from, to };
}

export function rangeMonths(months: number) {
  const to = new Date();
  return calculateMonthRange(to, months);
}

// Timezone options used by the TimeRangeSelector. We always include the
// special 'browser' option plus 'UTC'. When available, we expand to all
// IANA timezones supported by the runtime so users can pick any zone they
// care about.
const BASE_TIME_ZONES: { value: DisplayTimeZone; label: string }[] = [
  { value: 'browser', label: 'Browser (Local)' },
  { value: 'UTC', label: 'UTC' },
];

export const TIME_ZONE_OPTIONS: { value: DisplayTimeZone; label: string }[] =
  (() => {
    try {
      // Not all environments support Intl.supportedValuesOf, so guard it.
      const intlWithSupported = Intl as typeof Intl & {
        supportedValuesOf?: (key: 'timeZone') => readonly string[];
      };

      const supported =
        typeof intlWithSupported.supportedValuesOf === 'function'
          ? [...intlWithSupported.supportedValuesOf('timeZone')]
          : [];

      const dynamicZones = supported.map(tz => ({ value: tz, label: tz }));

      // Deduplicate in case any base entries overlap with dynamic ones.
      const seen = new Set<string>();
      const merged: { value: DisplayTimeZone; label: string }[] = [];

      for (const opt of [...BASE_TIME_ZONES, ...dynamicZones]) {
        if (seen.has(opt.value)) continue;
        seen.add(opt.value);
        merged.push(opt);
      }

      return merged;
    } catch {
      return BASE_TIME_ZONES;
    }
  })();
